import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import api from '../../../hooks/useApi';
import { canAccess } from '../../../utils/rbac';
import Modal from '../../shared/Modal';
import { useToast } from '../../ui/Toast';
import GanttChart from './GanttChart';
import TaskEditor from './TaskEditor';
import ResourceLoadPanel from './ResourceLoadPanel';
import StatusDatePanel from './StatusDatePanel';
import EarnedValuePanel from './EarnedValuePanel';
import ImportPlanPanel from './ImportPlanPanel';
import { dropMoves } from './outline';
import { toCsv } from '../../../utils/csv.js';
import {
  TASK_STATUS_LABELS, TASK_STATUS_COLORS, PHASES, SCHEDULE_STATE,
  fmtD, fmtDY, fmtDuration, fmtVariance, fmtDepLabel, assigneeLabel, hasExternal, externalLabel, EXTERNAL_BAR,
} from './planConstants';

/*
  The SOW plan.

  Structure is the WBS: any task can hold others, summary bars take their dates
  and progress from what is inside them, and the outline you arrange here is the
  order the Gantt draws. Dates are worked out on the server by the scheduling
  engine, so the table and the chart cannot disagree with each other or with a
  report.
*/

const VIEWS = [
  { id: 'table', label: '≡ Outline' },
  { id: 'gantt', label: '▦ Gantt' },
  { id: 'split', label: '⬓ Both' },
];

// canEdit is the 'sow:edit' grant, which every write behind this tab asks
// (pen test AD25996). Without it the plan reads, exports and prints, and offers
// nothing the server would refuse.
export default function ProjectPlanTab({ sowId, people = [], canEdit = false }) {
  const { toast } = useToast();
  const [plan, setPlan] = useState({ tasks: [], warnings: [], baseline: null, holidays: [] });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('split');
  const [collapsed, setCollapsed] = useState({});
  const [editTask, setEditTask] = useState(null);
  const [teams, setTeams] = useState([]);
  const [resourceLines, setResourceLines] = useState([]);
  const [parties, setParties] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [proposals, setProposals] = useState([]);
  // 'templates' | 'baselines' | 'milestones' | 'parties' | 'saveTemplate' | 'resource' | 'statusDate' | 'cost' | 'suggestion'
  const [panel, setPanel] = useState(null);
  const [baselinePrefill, setBaselinePrefill] = useState(null);   // { name, ccn_id }
  const [suggestion, setSuggestion] = useState(null);
  const [filter, setFilter] = useState({ q: '', quick: null, group: 'none' });
  const [selected, setSelected] = useState(() => new Set());
  const [ganttOptions, setGanttOptions] = useState({
    showBaseline: true, showCritical: true, showArrows: true, showProgress: true, showFloat: false,
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.get(`sows/${sowId}/project-tasks/plan`);
      setPlan(data || { tasks: [] });
    } catch (e) {
      toast.error(`Could not load the plan: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [sowId, toast]);

  const loadSide = useCallback(async () => {
    const [t, p, tpl, bl, prop, res] = await Promise.all([
      api.get('teams').catch(() => []),
      api.get('external-parties').catch(() => []),
      api.get('project-plan-templates').catch(() => []),
      api.get(`sows/${sowId}/plan-baselines`).catch(() => []),
      api.get(`sows/${sowId}/milestone-date-proposals`).catch(() => []),
      // The SOW's resource lines, so a task can say which one it delivers.
      api.get(`sows/${sowId}/resourcing`).catch(() => null),
    ]);
    setTeams(t || []); setParties(p || []); setTemplates(tpl || []);
    setBaselines(bl || []); setProposals(prop || []);
    setResourceLines(res?.lines || []);
  }, [sowId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSide(); }, [loadSide]);

  // Only worth asking for when there is nothing to show: an empty plan is the
  // only time this is an offer rather than a distraction.
  useEffect(() => {
    if (loading || (plan.tasks || []).length) { setSuggestion(null); return; }
    api.get(`sows/${sowId}/plan-suggestion`).then(setSuggestion).catch(() => setSuggestion(null));
  }, [sowId, loading, plan.tasks]);

  const tasks = plan.tasks || [];

  /* ── Mutations ── */

  const withBusy = async (fn, successMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      await load();
      await loadSide();
      if (successMsg) toast.success(successMsg);
      return r;
    } catch (e) {
      toast.error(e.message || 'That did not work.');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const saveTask = async (data) => {
    const id = editTask?.id;
    if (id) await api.put(`sows/${sowId}/project-tasks/${id}`, data);
    else {
      const r = await api.post(`sows/${sowId}/project-tasks`, data);
      // Inserted from a row: put it there rather than at the bottom, unless the
      // editor moved it under a different summary in the meantime.
      const at = editTask?.insertAt;
      if (at && r?.id && (data.parent_task_id || null) === at.parent_task_id) {
        const moves = dropMoves(tasks, r.id, at.targetId, at.where);
        if (moves) await api.post(`sows/${sowId}/project-tasks/outline`, { moves });
      }
    }
    setEditTask(null);
    await load();
    await loadSide();
  };

  const deleteTask = async (t) => {
    const kids = t.child_count || 0;
    const msg = kids
      ? `Remove "${t.name}"? The ${kids} task${kids !== 1 ? 's' : ''} under it will move up a level, not be deleted.`
      : `Remove "${t.name}"?`;
    if (!confirm(msg)) return;
    setEditTask(null);
    await withBusy(() => api.delete(`sows/${sowId}/project-tasks/${t.id}`));
  };

  const patchTask = (id, patch) =>
    withBusy(() => api.put(`sows/${sowId}/project-tasks/${id}`, patch));

  const moveOutline = (moves) =>
    withBusy(() => api.post(`sows/${sowId}/project-tasks/outline`, { moves }));

  /* ── Outline moves ── */

  // A new task on the row drawn directly below this one: the first child of an
  // open summary, otherwise the next sibling.
  const insertBelow = (t) => {
    const into = !!t.has_children && !collapsed[t.id];
    const parent_task_id = into ? t.id : (t.parent_task_id || null);
    setEditTask({ parent_task_id, insertAt: { targetId: t.id, where: into ? 'into' : 'after', parent_task_id } });
  };

  const dropRow = (id, targetId, where) => {
    const moves = dropMoves(tasks, id, targetId, where);
    if (moves) moveOutline(moves);
  };

  // Siblings, in the order they are drawn.
  const siblingsOf = useCallback((parentId) =>
    tasks.filter(t => (t.parent_task_id || null) === (parentId || null)), [tasks]);

  const moveUpDown = (task, dir) => {
    const sibs = siblingsOf(task.parent_task_id);
    const i = sibs.findIndex(s => s.id === task.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= sibs.length) return;
    const reordered = [...sibs];
    [reordered[i], reordered[j]] = [reordered[j], reordered[i]];
    moveOutline(reordered.map((s, k) => ({ id: s.id, sort_order: k })));
  };

  // Indent: become a child of the sibling immediately above. That is how every
  // outline works, and it is the only move that needs no drag target.
  const indent = (task) => {
    const sibs = siblingsOf(task.parent_task_id);
    const i = sibs.findIndex(s => s.id === task.id);
    if (i <= 0) return;
    const newParent = sibs[i - 1];
    const kids = siblingsOf(newParent.id);
    moveOutline([{ id: task.id, parent_task_id: newParent.id, sort_order: kids.length }]);
  };

  const outdent = (task) => {
    if (!task.parent_task_id) return;
    const parent = tasks.find(t => t.id === task.parent_task_id);
    if (!parent) return;
    const uncles = siblingsOf(parent.parent_task_id);
    const at = uncles.findIndex(s => s.id === parent.id);
    // Slot in directly after the old parent, so the row does not jump.
    const moves = uncles.slice(0, at + 1).map((s, k) => ({ id: s.id, sort_order: k }));
    moves.push({ id: task.id, parent_task_id: parent.parent_task_id || null, sort_order: at + 1 });
    uncles.slice(at + 1).forEach((s, k) => moves.push({ id: s.id, sort_order: at + 2 + k }));
    moveOutline(moves);
  };

  /* ── Baseline and milestones ── */

  const takeBaseline = async (name, note, ccnId) => {
    await withBusy(
      () => api.post(`sows/${sowId}/plan-baselines`, { name, note, ccn_id: ccnId || null }),
      'Baseline taken. Variance is now measured against this plan.',
    );
    setBaselinePrefill(null);
    setPanel(null);
  };

  const declineRebaseline = async (ccn) => {
    const reason = prompt(
      `Keep the current baseline despite ${ccn.ccn_number}?\n\nOptionally say why (this goes on the record):`,
      '',
    );
    if (reason === null) return;   // cancelled, so no decision recorded
    await withBusy(
      () => api.post(`sows/${sowId}/plan-baselines/decline-rebaseline`, { ccn_id: ccn.id, reason: reason || null }),
      `${ccn.ccn_number} noted. The baseline is unchanged.`,
    );
  };

  const acceptProposals = async (ids) => {
    const r = await withBusy(
      () => api.post(`sows/${sowId}/milestone-date-proposals/accept`, ids ? { milestone_ids: ids } : {}),
    );
    toast.success(`${r?.applied ?? 0} milestone forecast date${r?.applied === 1 ? '' : 's'} updated.`);
  };

  const applyTemplate = async (tpl) => {
    await withBusy(() => api.post(`sows/${sowId}/project-tasks/apply-template/${tpl.id}`, {}),
      `Applied ${tpl.name}.`);
    setPanel(null);
  };

  /* ── Outline level ──
     The single most useful control on a 200-line plan: collapse everything
     below level 1 and you are looking at eight deliverables instead of two
     hundred tasks. Project has had these buttons for thirty years. */
  const collapseToLevel = (level) => {
    if (level === null) { setCollapsed({}); return; }
    const next = {};
    for (const t of tasks) {
      if (t.has_children && (t.depth ?? 0) >= level - 1) next[t.id] = true;
    }
    setCollapsed(next);
  };
  const maxDepth = useMemo(() => tasks.reduce((m, t) => Math.max(m, t.depth ?? 0), 0), [tasks]);

  /* ── Bulk edit ── */

  const applyToSelected = async (patch, label) => {
    const ids = [...selected];
    if (!ids.length) return;
    await withBusy(
      () => Promise.all(ids.map(id => api.put(`sows/${sowId}/project-tasks/${id}`, patch))),
      `${label} on ${ids.length} task${ids.length !== 1 ? 's' : ''}.`,
    );
    setSelected(new Set());
  };

  // "Set team…" can also hand the selected tasks to an outside organisation:
  // the party joins whoever is already on each task, and the internal team
  // comes off, as it does in the task editor.
  const applyPartyToSelected = async (partyId) => {
    const ids = [...selected];
    const party = parties.find(p => p.id === partyId);
    if (!ids.length || !party) return;
    const byId = new Map(tasks.map(t => [t.id, t]));
    await withBusy(
      () => Promise.all(ids.map(id => {
        const current = (byId.get(id)?.assignees || []).map(a => ({
          person_id: a.person_id, external_party_id: a.external_party_id,
          fte_pct: a.fte_pct, planned_hours: a.planned_hours, is_primary: a.is_primary, note: a.note,
        }));
        const assignees = current.some(a => a.external_party_id === partyId)
          ? current : [...current, { external_party_id: partyId, fte_pct: 100 }];
        return api.put(`sows/${sowId}/project-tasks/${id}`, { team_id: null, assignees });
      })),
      `${party.short_code || party.name} set on ${ids.length} task${ids.length !== 1 ? 's' : ''}.`,
    );
    setSelected(new Set());
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!confirm(`Remove ${ids.length} task${ids.length !== 1 ? 's' : ''}? Anything nested under them moves up a level.`)) return;
    // One at a time: each delete re-parents children, and running them
    // together would race on the same rows.
    await withBusy(async () => {
      for (const id of ids) await api.delete(`sows/${sowId}/project-tasks/${id}`);
    }, `${ids.length} task${ids.length !== 1 ? 's' : ''} removed.`);
    setSelected(new Set());
  };

  /* ── Derived ── */

  // The tree, with anything under a collapsed summary hidden.
  const visible = useMemo(() => {
    const out = [];
    const hidden = new Set();
    for (const t of tasks) {
      if (t.parent_task_id && hidden.has(t.parent_task_id)) { hidden.add(t.id); continue; }
      out.push(t);
      if (collapsed[t.id]) hidden.add(t.id);
    }
    return out;
  }, [tasks, collapsed]);

  /* Filtering keeps the outline readable by carrying each match's ancestors
     with it. A task on its own, ripped out of the summary bar that gives it
     meaning, is worse than no filter at all. */
  const filterActive = !!(filter.q.trim() || filter.quick);
  const matches = useCallback((t) => {
    const q = filter.q.trim().toLowerCase();
    if (q) {
      const hay = [t.name, t.description, t.wbs_code, assigneeLabel(t), t.team_name]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    switch (filter.quick) {
      case 'late':       return t.schedule_state === 'behind' || t.days_late > 0;
      case 'critical':   return !!t.is_critical;
      case 'incomplete': return t.status !== 'completed' && t.status !== 'n_a' && !t.has_children;
      case 'milestones': return !!t.is_milestone;
      case 'external':   return hasExternal(t);
      case 'unassigned': return !t.has_children && !(t.assignees || []).length && !t.owner_id;
      default:           return true;
    }
  }, [filter]);

  const rows = useMemo(() => {
    if (filter.group !== 'none') return tasks;      // grouped view is flat
    if (!filterActive) return visible;

    const byId = new Map(tasks.map(t => [t.id, t]));
    const keep = new Set();
    for (const t of tasks) {
      if (!matches(t)) continue;
      keep.add(t.id);
      let p = t.parent_task_id;
      let guard = 0;
      while (p && guard++ < 50) { keep.add(p); p = byId.get(p)?.parent_task_id; }
    }
    return tasks.filter(t => keep.has(t.id));
  }, [tasks, visible, filterActive, matches, filter.group]);

  // Grouping abandons the hierarchy, the way Project's grouping does: you are
  // asking a different question, and the answer is a list per team or per owner.
  const groups = useMemo(() => {
    if (filter.group === 'none') return null;
    const keyOf = (t) => {
      if (filter.group === 'team') return t.team_name || (hasExternal(t) ? `External · ${externalLabel(t)}` : 'No team');
      if (filter.group === 'owner') return assigneeLabel(t) || 'Unassigned';
      if (filter.group === 'phase') return PHASES.find(p => p.id === (t.phase || 'execution'))?.label || 'Execution';
      if (filter.group === 'status') return TASK_STATUS_LABELS[t.status] || t.status;
      return 'All';
    };
    const map = new Map();
    for (const t of tasks) {
      if (t.has_children) continue;                 // a summary is not a member of a group
      if (filterActive && !matches(t)) continue;
      const k = keyOf(t);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(t);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [tasks, filter.group, filterActive, matches]);

  const stats = useMemo(() => {
    const leaves = tasks.filter(t => !t.has_children);
    return {
      total: leaves.length,
      done: leaves.filter(t => t.status === 'completed').length,
      behind: leaves.filter(t => t.schedule_state === 'behind').length,
      critical: leaves.filter(t => t.is_critical).length,
      milestones: leaves.filter(t => t.is_milestone).length,
    };
  }, [tasks]);

  /* ── Export ── */

  // CSV rather than a real xlsx: it opens in Excel, it is one function rather
  // than a dependency, and a plan in a pack is usually pasted into something
  // else anyway. Columns follow the outline on screen.
  const exportCsv = () => {
    const cols = [
      ['WBS', t => t.wbs_code],
      ['Task', t => `${'    '.repeat(t.depth || 0)}${t.name}`],
      ['Type', t => (t.is_milestone ? 'Milestone' : t.has_children ? 'Summary' : 'Task')],
      ['Who', t => assigneeLabel(t)],
      ['Team', t => t.team_name || ''],
      ['Duration (d)', t => (t.is_milestone ? '' : t.duration_days ?? '')],
      ['Effort (h)', t => t.effort_hours ?? ''],
      ['Booked (h)', t => t.actual_work_hours ?? ''],
      ['Start', t => t.start_date || ''],
      ['Finish', t => t.end_date || ''],
      ['Baseline start', t => t.baseline_start || ''],
      ['Baseline finish', t => t.baseline_finish || ''],
      ['Finish variance (d)', t => (t.finish_variance_days ?? '')],
      ['Actual start', t => t.actual_start || ''],
      ['Actual finish', t => t.actual_finish || ''],
      ['Status', t => TASK_STATUS_LABELS[t.status] || t.status],
      ['% complete', t => t.completion_pct ?? 0],
      ['% expected', t => t.expected_pct ?? ''],
      ['Float (d)', t => (t.total_float_days ?? '')],
      ['Critical', t => (t.is_critical ? 'Yes' : '')],
      ['Predecessors', t => (t.predecessors || [])
        .map(p => `${tasks.find(x => x.id === p.predecessor_id)?.wbs_code || '?'}${fmtDepLabel(p) === 'FS' ? '' : ` ${fmtDepLabel(p)}`}`)
        .join('; ')],
      ['Constraint', t => (t.constraint_type && t.constraint_type !== 'asap'
        ? `${t.constraint_type.toUpperCase()} ${t.constraint_date || ''}`.trim() : '')],
    ];
    const dataRows = (filterActive || filter.group !== 'none' ? rows : tasks)
      .map(t => cols.map(([, get]) => get(t)));
    // A BOM, or Excel mangles anything that is not ASCII.
    const csv = `\ufeff${toCsv(cols.map(([h]) => h), dataRows)}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plan-${sowId}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // The plan on paper. Fetched rather than linked so the auth header goes with
  // it, and so a failure is a toast rather than a browser tab of JSON.
  const downloadPdf = async (internal) => {
    setBusy(true);
    try {
      const blob = await api.getBlob(`sows/${sowId}/plan-pdf${internal ? '?internal=1' : ''}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Plan-${sowId}${internal ? '-internal' : ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e.message || 'Could not build the PDF.');
    } finally {
      setBusy(false);
    }
  };

  /* ── Keyboard ──
     Only when a single row is selected and focus is not in a field, so typing
     a task name never moves the outline underneath you. */
  useEffect(() => {
    if (!canEdit) return undefined;
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.metaKey || e.ctrlKey) return;
      const t = selected.size === 1 ? tasks.find(x => x.id === [...selected][0]) : null;
      if (e.key === 'n' && !e.altKey) { e.preventDefault(); t ? insertBelow(t) : setEditTask({}); return; }
      if (!t || !e.altKey) return;
      const actions = {
        ArrowUp:    () => moveUpDown(t, -1),
        ArrowDown:  () => moveUpDown(t, 1),
        ArrowRight: () => indent(t),
        ArrowLeft:  () => outdent(t),
      };
      if (actions[e.key]) { e.preventDefault(); actions[e.key](); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, selected, tasks, moveUpDown, indent, outdent, insertBelow]);

  const pendingProposals = proposals.filter(p => p.differs && !p.locked);
  const warnings = plan.warnings || [];

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Loading plan…</div>;

  return (
    <div className={busy ? 'opacity-70 pointer-events-none transition-opacity' : 'transition-opacity'}>

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[9px] text-gray-500">
            {stats.total} task{stats.total !== 1 ? 's' : ''} · {stats.done} complete
            {stats.milestones > 0 && <> · {stats.milestones} milestone{stats.milestones !== 1 ? 's' : ''}</>}
            {stats.behind > 0 && <span className="text-red-600 font-semibold"> · {stats.behind} behind</span>}
            {stats.critical > 0 && <span className="text-gray-400"> · {stats.critical} on the critical path</span>}
          </div>
          <div className="flex rounded border border-gray-200 overflow-hidden">
            {VIEWS.map(v => (
              <button key={v.id} onClick={() => setView(v.id)}
                      className={`px-2 py-1 text-[9px] font-semibold ${view === v.id ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                {v.label}
              </button>
            ))}
          </div>
          {plan.project_start && (
            <span className="text-[9px] text-gray-400">
              {fmtDY(plan.project_start)} → <span className="font-semibold text-gray-600">{fmtDY(plan.project_finish)}</span>
            </span>
          )}
        </div>
        <div className="flex gap-1.5 items-center flex-wrap">
          {!canEdit && (
            <span className="text-[9px] text-gray-400" title='Editing the plan needs the "Create and edit SOWs" grant in Admin → Access'>
              Read only
            </span>
          )}
          {canEdit && (<>
          <button onClick={() => setEditTask({})}
                  className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/90">+ Task</button>
          <button onClick={() => setEditTask({ is_milestone: 1, duration_days: 0 })}
                  className="px-2 py-1 text-[9px] font-semibold border border-gold/50 bg-gold/10 text-gold rounded hover:bg-gold/20">◆ Milestone</button>
          <button onClick={() => setPanel('templates')}
                  className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">Templates</button>
          <button onClick={() => setPanel('import')}
                  title="Bring in a plan saved from Microsoft Project as XML. You see the tree before anything is written."
                  className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">Import XML</button>
          <button onClick={() => setPanel('baselines')}
                  className={`px-2 py-1 text-[9px] font-semibold border rounded ${plan.baseline
                    ? 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
            {plan.baseline ? 'Baseline' : 'Set baseline'}
          </button>
          <button onClick={() => setPanel('milestones')}
                  className={`px-2 py-1 text-[9px] font-semibold border rounded ${pendingProposals.length
                    ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
            Milestones{pendingProposals.length ? ` (${pendingProposals.length})` : ''}
          </button>
          </>)}
          <button onClick={() => setPanel('resource')}
                  className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50"
                  title="Who is over-committed, counting every SOW they are on">
            Resource load
          </button>
          {canEdit && (
          <button onClick={() => setPanel('statusDate')}
                  className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50"
                  title="Move work that never started out of the past">
            Update progress
          </button>
          )}
          {/* Earned value is SOW money, the sow:see-financials grant (pen test AD26341, AD12/AD16). */}
          {canAccess('sow:see-financials') && (
          <button onClick={() => setPanel('cost')}
                  className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50"
                  title="Cost against the baseline: schedule and cost performance">
            Cost
          </button>
          )}
          {canEdit && (
          <label className="flex items-center gap-1 cursor-pointer px-1.5 py-1 border border-gray-200 rounded bg-white"
                 title="Email a person when they are newly assigned a task on this SOW. Off by default, because an email for every assignment is how a system gets muted.">
            <input type="checkbox" className="w-3 h-3 accent-navy"
                   checked={!!plan.notify_task_assignments}
                   onChange={e => withBusy(() => api.put(`sows/${sowId}`, { notify_task_assignments: e.target.checked ? 1 : 0 }))} />
            <span className="text-[9px] text-gray-600">Email on assignment</span>
          </label>
          )}
          <button onClick={exportCsv}
                  className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white text-gray-600 hover:bg-gray-50">
            CSV
          </button>
          <div className="flex rounded border border-gray-200 overflow-hidden">
            <button onClick={() => downloadPdf(false)}
                    title="Cover, chart and task list, without who is on what or progress. For a client."
                    className="px-2 py-1 text-[9px] font-semibold bg-white text-gray-600 hover:bg-gray-50">
              PDF
            </button>
            <button onClick={() => downloadPdf(true)}
                    title="The same, with owners and progress. For an internal pack."
                    className="px-2 py-1 text-[9px] font-semibold bg-white text-gray-600 hover:bg-gray-50 border-l border-gray-200">
              + internal
            </button>
          </div>
        </div>
      </div>

      {/* ── Find and group ── */}
      {tasks.length > 0 && (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <input
            value={filter.q}
            onChange={e => setFilter(f => ({ ...f, q: e.target.value }))}
            placeholder="Find a task, person or team…"
            className="text-[9px] border border-gray-200 rounded px-2 py-1 w-52 focus:outline-none focus:ring-1 focus:ring-navy"
          />
          <div className="flex rounded border border-gray-200 overflow-hidden">
            {[['late', 'Late'], ['critical', 'Critical'], ['incomplete', 'Open'],
              ['milestones', 'Milestones'], ['external', 'External'], ['unassigned', 'Unassigned']].map(([id, label]) => (
              <button key={id}
                      onClick={() => setFilter(f => ({ ...f, quick: f.quick === id ? null : id }))}
                      className={`px-1.5 py-1 text-[9px] font-semibold ${filter.quick === id
                        ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
          {maxDepth > 0 && (
            <div className="flex rounded border border-gray-200 overflow-hidden" title="Show the plan to this level of the breakdown">
              <span className="px-1.5 py-1 text-[8px] font-bold uppercase tracking-wider text-gray-400 bg-gray-50">Level</span>
              {[1, 2, 3].filter(l => l <= maxDepth + 1).map(l => (
                <button key={l} onClick={() => collapseToLevel(l)}
                        className="px-1.5 py-1 text-[9px] font-semibold bg-white text-gray-500 hover:bg-gray-50 border-l border-gray-200">
                  {l}
                </button>
              ))}
              <button onClick={() => collapseToLevel(null)}
                      className="px-1.5 py-1 text-[9px] font-semibold bg-white text-gray-500 hover:bg-gray-50 border-l border-gray-200">
                All
              </button>
            </div>
          )}
          <select value={filter.group} onChange={e => setFilter(f => ({ ...f, group: e.target.value }))}
                  className="text-[9px] border border-gray-200 rounded px-1 py-1 bg-white text-gray-600">
            <option value="none">No grouping</option>
            <option value="team">Group by team</option>
            <option value="owner">Group by who</option>
            <option value="phase">Group by phase</option>
            <option value="status">Group by status</option>
          </select>
          {(filterActive || filter.group !== 'none') && (
            <button onClick={() => setFilter({ q: '', quick: null, group: 'none' })}
                    className="text-[9px] text-gray-400 hover:text-navy">clear</button>
          )}
          {filterActive && (
            <span className="text-[8px] text-gray-400">
              {rows.filter(t => matches(t)).length} match{rows.filter(t => matches(t)).length === 1 ? '' : 'es'}
            </span>
          )}
          {canEdit && (
          <span className="ml-auto text-[8px] text-gray-300">
            drag ⋮⋮ to move a row · n new task, below the selected row if there is one · alt + arrows to move, indent and outdent
          </span>
          )}
        </div>
      )}

      {/* ── Bulk edit ── */}
      {canEdit && selected.size > 0 && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-navy/5 border border-navy/20 rounded flex-wrap">
          <span className="text-[9px] font-semibold text-navy">
            {selected.size} selected
          </span>
          <select className="text-[9px] border border-gray-200 rounded px-1 py-0.5 bg-white" defaultValue=""
                  onChange={e => {
                    const v = e.target.value;
                    if (v.startsWith('party:')) applyPartyToSelected(v.slice(6));
                    else if (v) applyToSelected({ team_id: v }, 'Team set');
                    e.target.value = '';
                  }}>
            <option value="">Set team…</option>
            <optgroup label="Teams">
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </optgroup>
            {parties.length > 0 && (
              <optgroup label="External">
                {parties.map(p => <option key={p.id} value={`party:${p.id}`}>{p.short_code ? `${p.short_code} · ` : ''}{p.name}</option>)}
              </optgroup>
            )}
          </select>
          <select className="text-[9px] border border-gray-200 rounded px-1 py-0.5 bg-white" defaultValue=""
                  onChange={e => { if (e.target.value) { applyToSelected({ owner_id: e.target.value }, 'Owner set'); e.target.value = ''; } }}>
            <option value="">Set owner…</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
          <select className="text-[9px] border border-gray-200 rounded px-1 py-0.5 bg-white" defaultValue=""
                  onChange={e => { if (e.target.value) { applyToSelected({ status: e.target.value }, 'Status set'); e.target.value = ''; } }}>
            <option value="">Set status…</option>
            {Object.entries(TASK_STATUS_LABELS).map(([s, l]) => <option key={s} value={s}>{l}</option>)}
          </select>
          <select className="text-[9px] border border-gray-200 rounded px-1 py-0.5 bg-white" defaultValue=""
                  onChange={e => { if (e.target.value) { applyToSelected({ phase: e.target.value }, 'Phase set'); e.target.value = ''; } }}>
            <option value="">Set phase…</option>
            {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <button onClick={deleteSelected}
                  className="text-[9px] font-semibold px-2 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50">
            Delete
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-[9px] text-gray-400 hover:text-navy">
            clear selection
          </button>
        </div>
      )}

      {/* ── Warnings ── */}
      {plan.cycle && (
        <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-[9px] text-red-700">
          <span className="font-bold">The plan cannot be scheduled.</span> {plan.cycle.message} Tasks caught in the loop are
          highlighted below. Remove one of their predecessors to break it.
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded space-y-0.5">
          {warnings.slice(0, 5).map((w, i) => (
            <div key={i} className="text-[9px] text-amber-800">{w.message}</div>
          ))}
          {warnings.length > 5 && <div className="text-[8px] text-amber-600">and {warnings.length - 5} more</div>}
        </div>
      )}
      {/* A change note moved the schedule. A prompt, not a gate: the plan can
          deliberately stay on its original baseline, and saying so is recorded. */}
      {(plan.rebaseline_prompts || []).map(ccn => (
        <div key={ccn.id} className="mb-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded flex items-center gap-2">
          <span className="text-[9px] text-violet-900 flex-1">
            <span className="font-semibold">{ccn.ccn_number}</span>
            {ccn.title ? ` — ${ccn.title}` : ''} was approved with a schedule change
            {ccn.approved_at ? ` on ${fmtDY(ccn.approved_at.slice(0, 10))}` : ''}.
            {plan.baseline
              ? ` Variance is still measured against "${plan.baseline.name}".`
              : ' This plan has no baseline yet.'}
          </span>
          {canEdit && (<>
          <button onClick={() => { setBaselinePrefill({ name: `Re-baseline ${ccn.ccn_number}`, ccn_id: ccn.id }); setPanel('baselines'); }}
                  className="text-[9px] font-semibold px-2 py-0.5 rounded bg-violet-600 text-white hover:bg-violet-700 flex-shrink-0">
            Re-baseline
          </button>
          <button onClick={() => declineRebaseline(ccn)}
                  className="text-[9px] font-semibold px-2 py-0.5 rounded border border-violet-300 text-violet-700 hover:bg-violet-100 flex-shrink-0">
            Keep current
          </button>
          </>)}
        </div>
      ))}
      {pendingProposals.length > 0 && (
        <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded flex items-center gap-2">
          <span className="text-[9px] text-blue-800 flex-1">
            The plan now finishes on a different date from{' '}
            <span className="font-semibold">{pendingProposals.length} payment milestone{pendingProposals.length !== 1 ? 's' : ''}</span>.
            Nothing has moved: forecast dates feed expected income, so they change when you say so.
          </span>
          {canEdit && (
          <button onClick={() => setPanel('milestones')}
                  className="text-[9px] font-semibold px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700">Review</button>
          )}
        </div>
      )}

      {tasks.length === 0 ? (
        <EmptyPlan
          canEdit={canEdit}
          onAdd={() => setEditTask({})}
          onTemplates={() => setPanel('templates')}
          suggestion={suggestion}
          onBuild={() => setPanel('suggestion')}
        />
      ) : (
        <div className="space-y-2">
          {(view === 'table' || view === 'split') && (
            <WbsTable
              rows={rows}
              groups={groups}
              allTasks={tasks}
              collapsed={collapsed}
              cycleIds={new Set(plan.cycle?.task_ids || [])}
              baseline={plan.baseline}
              selected={selected}
              onSelect={setSelected}
              filterActive={filterActive}
              readOnly={!canEdit}
              onToggle={id => setCollapsed(c => ({ ...c, [id]: !c[id] }))}
              onEdit={setEditTask}
              onPatch={patchTask}
              onIndent={indent}
              onOutdent={outdent}
              onMove={moveUpDown}
              onAddChild={parent => setEditTask({ parent_task_id: parent.id })}
              onInsertBelow={insertBelow}
              onDropRow={dropRow}
            />
          )}
          {(view === 'gantt' || view === 'split') && (
            <GanttChart
              tasks={filterActive && filter.group === 'none' ? rows : tasks}
              today={plan.today}
              holidays={plan.holidays}
              baseline={plan.baseline}
              collapsed={collapsed}
              onToggleCollapse={id => setCollapsed(c => ({ ...c, [id]: !c[id] }))}
              onEdit={canEdit ? setEditTask : undefined}
              onRescheduleTask={canEdit ? (id, patch) => {
                // Dragging a task that follows something has to say what it
                // means, or the engine simply recomputes it back and the drag
                // looks broken. It becomes a visible "start no earlier than"
                // constraint, shown as a chip on the row and removable from the
                // editor. A task with no predecessors just keeps its date.
                const t = tasks.find(x => x.id === id);
                if (patch.start_date && t?.predecessors?.length) {
                  return patchTask(id, { ...patch, constraint_type: 'snet', constraint_date: patch.start_date });
                }
                return patchTask(id, patch);
              } : undefined}
              options={ganttOptions}
              onOptionsChange={setGanttOptions}
            />
          )}
        </div>
      )}

      {/* ── Modals ── */}
      {canEdit && editTask && (
        <TaskEditor
          task={editTask}
          tasks={tasks}
          people={people}
          externalParties={parties}
          teams={teams}
          resourceLines={resourceLines}
          holidays={plan.holidays}
          onClose={() => setEditTask(null)}
          onSave={saveTask}
          onDelete={editTask.id ? deleteTask : null}
          onCreateParty={() => setPanel('parties')}
          onPartiesChanged={loadSide}
          onSplit={async (parts) => {
            const r = await api.post(`sows/${sowId}/project-tasks/${editTask.id}/split-by-team`, { parts });
            setEditTask(null);
            await load();
            await loadSide();
            toast.success(`Split into ${r.parts.length} tasks, one per team${r.kept_on_summary?.length ? `; ${r.kept_on_summary.length} ${r.kept_on_summary.length === 1 ? 'person stays' : 'people stay'} on the summary bar as an allowance` : ''}.`);
          }}
        />
      )}
      {panel === 'templates' && (
        <TemplatePanel
          templates={templates}
          hasTasks={tasks.length > 0}
          onApply={applyTemplate}
          onSaveAs={() => setPanel('saveTemplate')}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'import' && (
        <ImportPlanPanel
          sowId={sowId}
          onClose={() => setPanel(null)}
          onImported={async (r) => {
            setPanel(null);
            await load();
            await loadSide();
            const n = r?.created ?? 0;
            toast.success(r?.mode === 'replace'
              ? `Plan replaced: ${n} task${n === 1 ? '' : 's'} imported.`
              : `${n} task${n === 1 ? '' : 's'} imported below the plan.`);
          }}
        />
      )}
      {panel === 'saveTemplate' && (
        <SaveTemplatePanel sowId={sowId} taskCount={tasks.length}
                           onClose={() => setPanel(null)}
                           onSaved={() => { setPanel(null); loadSide(); }} />
      )}
      {panel === 'baselines' && (
        <BaselinePanel
          baselines={baselines}
          current={plan.baseline}
          tasks={tasks}
          prefill={baselinePrefill}
          onTake={takeBaseline}
          onSetCurrent={async (id) => { await withBusy(() => api.post(`sows/${sowId}/plan-baselines/${id}/set-current`, {})); }}
          onClose={() => { setBaselinePrefill(null); setPanel(null); }}
        />
      )}
      {panel === 'milestones' && (
        <MilestonePanel
          proposals={proposals}
          tasks={tasks}
          onLink={async (milestoneId, taskId) => {
            await withBusy(() => api.put(`milestones/${milestoneId}`, { task_id: taskId || null }));
          }}
          onAccept={acceptProposals}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'parties' && (
        <PartiesPanel parties={parties} onClose={() => setPanel(null)} onChanged={loadSide} />
      )}
      {panel === 'resource' && (
        <ResourceLoadPanel
          sowId={sowId}
          onClose={() => setPanel(null)}
          onOpenTask={canEdit ? id => { const t = tasks.find(x => x.id === id); if (t) { setPanel(null); setEditTask(t); } } : undefined}
        />
      )}
      {panel === 'suggestion' && suggestion && (
        <SuggestionPanel
          suggestion={suggestion}
          onClose={() => setPanel(null)}
          onBuild={async (ids) => {
            await withBusy(() => api.post(`sows/${sowId}/plan-suggestion`, { groups: ids }),
              'Plan created from the milestones.');
            setPanel(null);
          }}
        />
      )}
      {panel === 'cost' && canAccess('sow:see-financials') && (
        <EarnedValuePanel
          sowId={sowId}
          onClose={() => setPanel(null)}
          onOpenTask={canEdit ? id => { const t = tasks.find(x => x.id === id); if (t) { setPanel(null); setEditTask(t); } } : undefined}
        />
      )}
      {panel === 'statusDate' && (
        <StatusDatePanel
          sowId={sowId}
          statusDate={plan.status_date}
          onClose={() => setPanel(null)}
          onApplied={async (r) => {
            setPanel(null);
            await load();
            toast.success(`${r?.moved ?? 0} task${r?.moved === 1 ? '' : 's'} moved forward.`);
          }}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   The outline
   ──────────────────────────────────────────────────────────── */

function WbsTable({ rows, groups, allTasks, collapsed, cycleIds, baseline, selected, onSelect,
                   filterActive, readOnly = false, onToggle, onEdit, onPatch, onIndent, onOutdent, onMove, onAddChild,
                   onInsertBelow, onDropRow }) {
  const [showBaseline, setShowBaseline] = useState(!!baseline);
  const [drag, setDrag] = useState(null);   // { id, overId, where }
  const [showWork, setShowWork] = useState(false);

  const shown = groups ? groups.flatMap(([, list]) => list) : rows;
  const allSelected = shown.length > 0 && shown.every(t => selected.has(t.id));
  const toggleAll = () => onSelect(allSelected ? new Set() : new Set(shown.map(t => t.id)));
  const toggleOne = (id) => onSelect(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const rowProps = (t) => ({
    key: t.id,
    task: t,
    allTasks,
    collapsed,
    inCycle: cycleIds.has(t.id),
    showBaseline,
    showWork,
    readOnly,
    flat: !!groups,
    selected: selected.has(t.id),
    onToggleSelect: toggleOne,
    onToggle, onEdit, onPatch, onIndent, onOutdent, onMove, onAddChild, onInsertBelow,
    dragging: drag?.id === t.id,
    dropWhere: drag && drag.overId === t.id ? drag.where : null,
    onDragStart: id => setDrag({ id }),
    onDragOverRow: (overId, where) => setDrag(d => (d && (d.overId !== overId || d.where !== where) ? { ...d, overId, where } : d)),
    onDropRow: () => { if (drag?.overId) onDropRow(drag.id, drag.overId, drag.where); setDrag(null); },
    onDragEnd: () => setDrag(null),
  });

  const colCount = 12 + (showBaseline ? 2 : 0) + (showWork ? 3 : 0);

  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden">
      <div className="flex items-center gap-3 px-2 py-1 border-b border-gray-100 bg-gray-50">
        <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400">
          {groups ? 'Grouped' : 'Work breakdown'}
        </span>
        {filterActive && !groups && (
          <span className="text-[8px] text-gray-400">summary bars are kept for context</span>
        )}
        <label className="flex items-center gap-1 cursor-pointer ml-auto">
          <input type="checkbox" className="w-3 h-3 accent-navy" checked={showWork}
                 onChange={e => setShowWork(e.target.checked)} />
          <span className="text-[9px] text-gray-600">Work columns</span>
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="checkbox" className="w-3 h-3 accent-navy" checked={showBaseline}
                 onChange={e => setShowBaseline(e.target.checked)} disabled={!baseline} />
          <span className="text-[9px] text-gray-600">Baseline columns</span>
        </label>
        {baseline && <span className="text-[8px] text-gray-400">{baseline.name} · {fmtDY(baseline.taken_at?.slice(0, 10))}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-1 py-1 w-6">
                {!readOnly && (
                <input type="checkbox" className="w-3 h-3 accent-navy" checked={allSelected}
                       onChange={toggleAll} title="Select every row shown" />
                )}
              </th>
              <Th className="w-10">WBS</Th>
              <Th>Task</Th>
              <Th className="w-24">Who</Th>
              <Th className="w-16 text-right">Duration</Th>
              {showWork && <Th className="w-14 text-right">Effort</Th>}
              {showWork && <Th className="w-14 text-right">Booked</Th>}
              {showWork && <Th className="w-14 text-right">Units</Th>}
              <Th className="w-20">Start</Th>
              <Th className="w-20">Finish</Th>
              {showBaseline && <Th className="w-20">Baseline finish</Th>}
              {showBaseline && <Th className="w-16 text-right">Variance</Th>}
              <Th className="w-24">Predecessors</Th>
              <Th className="w-32">Progress</Th>
              <Th className="w-12 text-right">Float</Th>
              <Th className="w-20"></Th>
            </tr>
          </thead>
          <tbody>
            {groups
              ? groups.map(([label, list]) => (
                  <Fragment key={label}>
                    <tr className="bg-gray-100/70">
                      <td colSpan={colCount} className="px-2 py-1">
                        <span className="text-[9px] font-bold text-gray-600">{label}</span>
                        <span className="text-[8px] text-gray-400 ml-2">{list.length} task{list.length !== 1 ? 's' : ''}</span>
                        <span className="text-[8px] text-gray-400 ml-2">
                          {list.reduce((n, t) => n + (Number(t.duration_days) || 0), 0).toFixed(1)}d
                        </span>
                      </td>
                    </tr>
                    {list.map(t => <WbsRow {...rowProps(t)} />)}
                  </Fragment>
                ))
              : rows.map(t => <WbsRow {...rowProps(t)} />)}
            {shown.length === 0 && (
              <tr><td colSpan={colCount} className="px-3 py-6 text-center text-[9px] text-gray-400">
                Nothing matches that.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = '' }) => (
  <th className={`px-2 py-1 text-left text-[7.5px] uppercase text-gray-400 font-bold ${className}`}>{children}</th>
);

function WbsRow({ task: t, allTasks, inCycle, showBaseline, showWork, readOnly, flat, selected, onToggleSelect,
                 onToggle, onEdit, onPatch, onIndent, onOutdent, onMove, onAddChild, onInsertBelow, collapsed,
                 dragging, dropWhere, onDragStart, onDragOverRow, onDropRow, onDragEnd }) {
  const state = SCHEDULE_STATE[t.schedule_state] || SCHEDULE_STATE.not_started;
  const isSummary = t.has_children || t.is_summary;
  const overBooked = t.actual_work_hours > 0 && t.effort_hours > 0 && t.actual_work_hours > t.effort_hours;
  const external = hasExternal(t) && !isSummary;
  const shadows = [
    // The drop line is drawn where the row will land: above this one, or below it.
    dropWhere && `inset 0 ${dropWhere === 'before' ? 2 : -2}px 0 var(--gold)`,
    external && `inset 3px 0 0 ${EXTERNAL_BAR.fill}`,
  ].filter(Boolean);

  return (
    <tr className={`border-b border-gray-50 hover:bg-gray-50/60 group ${inCycle ? 'bg-red-50' : ''} ${
      selected ? 'bg-navy/5' : external ? 'bg-amber-50' : ''} ${t.status === 'completed' || dragging ? 'opacity-60' : ''}`}
        style={shadows.length ? { boxShadow: shadows.join(', ') } : undefined}
        onDragOver={flat || readOnly ? undefined : e => {
          e.preventDefault();
          const r = e.currentTarget.getBoundingClientRect();
          const where = e.clientY < r.top + r.height / 2 ? 'before'
            : t.has_children && !collapsed?.[t.id] ? 'into' : 'after';
          onDragOverRow(t.id, where);
        }}
        onDrop={flat || readOnly ? undefined : e => { e.preventDefault(); onDropRow(); }}>
      <td className="px-1 py-1 align-top">
        {!readOnly && (
        <div className="flex items-center gap-0.5">
          {!flat && (
            <span draggable title="Drag to move this task"
                  onDragStart={e => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', t.id);
                    e.dataTransfer.setDragImage(e.currentTarget.closest('tr'), 8, 8);
                    onDragStart(t.id);
                  }}
                  onDragEnd={onDragEnd}
                  className="cursor-grab text-[9px] leading-none text-gray-300 hover:text-navy select-none">⋮⋮</span>
          )}
          <input type="checkbox" className="w-3 h-3 accent-navy"
                 checked={selected} onChange={() => onToggleSelect(t.id)} />
        </div>
        )}
      </td>
      <td className="px-2 py-1 text-[8px] text-gray-300 font-mono tabular-nums align-top">{t.wbs_code}</td>

      <td className="px-2 py-1 align-top">
        <div className="flex items-start gap-1">
          <span style={{ width: flat ? 0 : t.depth * 12 }} className="flex-shrink-0" />
          {t.has_children ? (
            <button onClick={() => onToggle(t.id)} className="text-[8px] text-gray-400 hover:text-navy w-3 flex-shrink-0 mt-0.5">
              {collapsed?.[t.id] ? '▶' : '▼'}
            </button>
          ) : <span className="w-3 flex-shrink-0" />}
          <div className="min-w-0">
            <button onClick={() => { if (!readOnly) onEdit(t); }} className={readOnly ? 'text-left cursor-default' : 'text-left'}>
              <span className={`text-[9px] ${isSummary ? 'font-bold text-gray-800' : 'text-gray-700'} ${
                t.status === 'completed' ? 'line-through' : ''} ${readOnly ? '' : 'hover:text-gold'}`}>
                {!!t.is_milestone && <span className="text-gold mr-0.5">◆</span>}
                {t.name}
              </span>
            </button>
            <div className="flex items-center gap-1 flex-wrap mt-0.5">
              {!!t.is_critical && <Chip className="bg-red-100 text-red-700">critical</Chip>}
              {t.constraint_type && t.constraint_type !== 'asap' && (
                <Chip className="bg-amber-100 text-amber-700" title={`${t.constraint_type.toUpperCase()} ${t.constraint_date || ''}`}>
                  {t.constraint_type === 'snet' ? 'not before' : t.constraint_type === 'mso' ? 'pinned' : 'deadline'}
                </Chip>
              )}
              {hasExternal(t) && <Chip className="bg-amber-500 text-white font-bold">external · {externalLabel(t)}</Chip>}
              {t.duration_unit === 'calendar' && <Chip className="bg-gray-100 text-gray-500">elapsed</Chip>}
              {t.task_type === 'checklist' && <Chip className="bg-violet-100 text-violet-600">checklist</Chip>}
              {t.actual_start && !t.actual_finish && <Chip className="bg-blue-100 text-blue-700">started {fmtD(t.actual_start)}</Chip>}
              {t.actual_finish && <Chip className="bg-emerald-100 text-emerald-700">finished {fmtD(t.actual_finish)}</Chip>}
            </div>
          </div>
        </div>
      </td>

      <td className="px-2 py-1 text-[8px] text-gray-600 align-top">
        {assigneeLabel(t) || <span className="text-gray-300">—</span>}
        {t.team_name && <div className="text-[7px] text-gray-400">{t.team_name}</div>}
      </td>

      <td className="px-2 py-1 text-[8px] text-gray-600 text-right align-top tabular-nums">
        {t.is_milestone ? '—' : fmtDuration(t.duration_days, t.duration_unit)}
        {!!t.effort_driven && <div className="text-[6.5px] text-violet-500 font-semibold">from effort</div>}
      </td>
      {showWork && (
        <td className="px-2 py-1 text-[8px] text-gray-600 text-right align-top tabular-nums">
          {t.effort_hours ? `${round1(t.effort_hours)}h` : <span className="text-gray-300">—</span>}
        </td>
      )}
      {showWork && (
        <td className="px-2 py-1 text-[8px] text-right align-top tabular-nums">
          {t.actual_work_hours
            ? <span className={overBooked ? 'text-red-600 font-semibold' : 'text-gray-600'}
                    title={overBooked ? 'More hours booked than estimated' : 'Hours booked to this task'}>
                {round1(t.actual_work_hours)}h
              </span>
            : <span className="text-gray-300">—</span>}
        </td>
      )}
      {showWork && (
        <td className="px-2 py-1 text-[8px] text-gray-500 text-right align-top tabular-nums">
          {t.assigned_units ? `${t.assigned_units}` : <span className="text-gray-300">—</span>}
        </td>
      )}
      <td className="px-2 py-1 text-[8px] text-gray-600 align-top whitespace-nowrap tabular-nums">{fmtD(t.start_date)}</td>
      <td className="px-2 py-1 text-[8px] text-gray-600 align-top whitespace-nowrap tabular-nums">{fmtD(t.end_date)}</td>

      {showBaseline && (
        <td className="px-2 py-1 text-[8px] text-gray-400 align-top whitespace-nowrap tabular-nums">{fmtD(t.baseline_finish)}</td>
      )}
      {showBaseline && (
        <td className="px-2 py-1 text-[8px] text-right align-top tabular-nums">
          {t.finish_variance_days == null ? <span className="text-gray-300">—</span> : (
            <span className={t.finish_variance_days > 0 ? 'text-red-600 font-semibold'
              : t.finish_variance_days < 0 ? 'text-emerald-600' : 'text-gray-400'}>
              {t.finish_variance_days > 0 ? '+' : ''}{t.finish_variance_days}d
            </span>
          )}
        </td>
      )}

      <td className="px-2 py-1 text-[7.5px] text-gray-500 align-top">
        {(t.predecessors || []).length === 0 ? <span className="text-gray-300">—</span> : (
          <div className="space-y-0.5">
            {t.predecessors.map(p => {
              const pred = allTasks.find(x => x.id === p.predecessor_id);
              return (
                <div key={p.predecessor_id} className="truncate" title={`${pred?.name || ''} — ${fmtDepLabel(p)}`}>
                  <span className="font-mono text-gray-400">{pred?.wbs_code}</span>{' '}
                  <span className="font-semibold text-gray-500">{fmtDepLabel(p)}</span>
                </div>
              );
            })}
          </div>
        )}
      </td>

      <td className="px-2 py-1 align-top">
        {isSummary ? (
          <div className="flex items-center gap-1.5">
            <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <div className="h-full rounded-full bg-slate-500" style={{ width: `${t.completion_pct || 0}%` }} />
            </div>
            <span className="text-[7.5px] font-semibold text-gray-500 w-7 text-right">{t.completion_pct || 0}%</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            {readOnly ? (
              <span className="text-[8px] font-semibold" style={{ color: TASK_STATUS_COLORS[t.status] }}>
                {TASK_STATUS_LABELS[t.status] || t.status}
              </span>
            ) : (
            <select
              value={t.status}
              onChange={e => onPatch(t.id, { status: e.target.value })}
              className="text-[8px] border rounded px-1 py-0.5 bg-white"
              style={{ color: TASK_STATUS_COLORS[t.status] }}
            >
              {Object.entries(TASK_STATUS_LABELS).map(([s, l]) => <option key={s} value={s}>{l}</option>)}
            </select>
            )}
            {!t.is_milestone && (
              <>
                <span className={`text-[7.5px] font-bold w-7 text-right ${state.text}`}>
                  {t.completion_pct || 0}%
                </span>
                {t.expected_pct != null && t.schedule_state !== 'not_started' && (
                  <span className={`text-[7px] px-1 py-0.5 rounded font-semibold ${state.bg}`}
                        title={`Should be about ${t.expected_pct}% by today`}>
                    {t.schedule_state === 'behind' && t.days_late ? `${t.days_late}d late` : state.label}
                  </span>
                )}
                {/* What the booked time says, offered rather than imposed:
                    a task can be 90% done on a fifth of the hours, and only
                    the person doing it knows which. */}
                {!readOnly && t.suggested_pct != null && t.suggested_pct !== (t.completion_pct || 0) && t.status !== 'completed' && (
                  <button
                    onClick={() => onPatch(t.id, { completion_pct: t.suggested_pct, status: t.status === 'not_started' ? 'in_progress' : t.status })}
                    title={`${round1(t.actual_work_hours)}h booked of ${round1(t.effort_hours)}h estimated. Click to set it.`}
                    className="text-[7px] px-1 py-0.5 rounded font-semibold bg-violet-100 text-violet-700 hover:bg-violet-200">
                    time says {t.suggested_pct}%
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </td>

      <td className="px-2 py-1 text-[8px] text-right align-top tabular-nums">
        {t.total_float_days == null ? <span className="text-gray-300">—</span> : (
          <span className={t.total_float_days <= 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}>
            {t.total_float_days}d
          </span>
        )}
      </td>

      <td className="px-1 py-1 align-top">
        {!readOnly && (
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconBtn title="Move up" onClick={() => onMove(t, -1)}>↑</IconBtn>
          <IconBtn title="Move down" onClick={() => onMove(t, 1)}>↓</IconBtn>
          <IconBtn title="Indent — put this under the task above" onClick={() => onIndent(t)}>→</IconBtn>
          <IconBtn title="Outdent" onClick={() => onOutdent(t)} disabled={!t.parent_task_id}>←</IconBtn>
          <IconBtn title="Add a task under this one" onClick={() => onAddChild(t)}>+</IconBtn>
          {!flat && (
            <button onClick={() => onInsertBelow(t)} title="Insert a new task directly below this row"
                    className="h-4 px-1 flex items-center text-[8px] font-semibold whitespace-nowrap rounded border border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-navy">
              + below
            </button>
          )}
        </div>
        )}
      </td>
    </tr>
  );
}

const round1 = (n) => (Number.isFinite(Number(n)) ? Math.round(Number(n) * 10) / 10 : n);

const Chip = ({ children, className = '', title }) => (
  <span title={title} className={`text-[6.5px] px-1 py-0.5 rounded font-semibold ${className}`}>{children}</span>
);

const IconBtn = ({ children, onClick, title, disabled }) => (
  <button onClick={onClick} title={title} disabled={disabled}
          className="w-4 h-4 flex items-center justify-center text-[9px] rounded border border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-navy disabled:opacity-20">
    {children}
  </button>
);

function EmptyPlan({ canEdit, onAdd, onTemplates, suggestion, onBuild }) {
  const groups = canEdit ? (suggestion?.groups || []) : [];
  if (!canEdit) {
    return (
      <div className="bg-white border border-gray-200 rounded p-8 text-center">
        <div className="text-[11px] font-semibold text-gray-600 mb-1">No plan yet</div>
        <div className="text-[9px] text-gray-400 max-w-md mx-auto">
          Whoever builds this SOW's plan will add it here. Building one needs the "Create and edit SOWs" grant.
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-gray-200 rounded p-8 text-center">
      <div className="text-[11px] font-semibold text-gray-600 mb-1">No plan yet</div>
      <div className="text-[9px] text-gray-400 mb-3 max-w-md mx-auto">
        Build the work breakdown first: a summary bar per deliverable or payment milestone, with the
        detail nested underneath. Dates follow from durations and the links between tasks.
      </div>

      {/* Starting from nothing is where most plans die. The milestones are
          already here and already dated, so there is no reason to. */}
      {groups.length > 0 && (
        <div className="max-w-lg mx-auto mb-4 border border-gold/40 bg-gold/5 rounded p-3 text-left">
          <div className="text-[10px] font-bold text-gold mb-1">Start from the milestones</div>
          <div className="text-[9px] text-gray-600 mb-2">
            This SOW has <span className="font-semibold">{groups.length} dated milestone{groups.length !== 1 ? 's' : ''}</span>.
            They can become a summary bar each, with a delivery task and a completion milestone landing on
            the date finance is already forecasting, so the plan and the forecast agree from day one.
          </div>
          <button onClick={onBuild}
                  className="px-3 py-1 text-[10px] font-semibold bg-gold text-white rounded hover:bg-gold/90">
            Build a plan from {groups.length} milestone{groups.length !== 1 ? 's' : ''} →
          </button>
        </div>
      )}

      <div className="flex gap-2 justify-center">
        <button onClick={onAdd} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">+ First task</button>
        <button onClick={onTemplates} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white text-gray-600">Start from a template</button>
      </div>
      {suggestion && groups.length === 0 && suggestion.reason && (
        <div className="text-[8px] text-gray-400 mt-3">{suggestion.reason}</div>
      )}
    </div>
  );
}

function SuggestionPanel({ suggestion, onClose, onBuild }) {
  const [chosen, setChosen] = useState(() => new Set(suggestion.groups.map(g => g.milestone_id)));
  const toggle = (id) => setChosen(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  return (
    <Modal title="Build a plan from the milestones" onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => onBuild([...chosen])} disabled={!chosen.size}
                className="px-3 py-1 text-[10px] font-semibold bg-gold text-white rounded disabled:opacity-40">
          Build {chosen.size} summary bar{chosen.size !== 1 ? 's' : ''}
        </button>
      </>
    }>
      <div className="space-y-3">
        <div className="text-[8px] text-gray-500 bg-gray-50 rounded px-2 py-1.5">
          A starting point, not a finished plan. Each milestone becomes a summary bar holding one delivery
          task and a completion milestone on the forecast date, and the payment milestone is linked to it, so
          the plan drives its date from then on. Durations are the gap between one milestone and the next,
          which is the only honest guess available until somebody says how long the work takes. Break the
          delivery tasks down and the dates start meaning something.
        </div>

        <div className="overflow-x-auto"><table className="w-full">
          <thead><tr className="border-b border-gray-100">
            <Th className="w-6"></Th><Th className="w-16">Code</Th><Th>Becomes</Th>
            <Th className="w-20">Starts</Th><Th className="w-20">Milestone</Th><Th className="w-14 text-right">Days</Th>
          </tr></thead>
          <tbody>
            {suggestion.groups.map(g => (
              <tr key={g.milestone_id} className={`border-b border-gray-50 ${g.tight ? 'bg-amber-50/60' : ''}`}>
                <td className="px-2 py-1">
                  <input type="checkbox" className="w-3 h-3 accent-gold"
                         checked={chosen.has(g.milestone_id)} onChange={() => toggle(g.milestone_id)} />
                </td>
                <td className="px-2 py-1 text-[9px] font-semibold text-navy whitespace-nowrap">
                  {g.milestone_code}
                  {g.locked && <div className="text-[7px] text-gray-400 font-normal">already billed</div>}
                </td>
                <td className="px-2 py-1 text-[8.5px] text-gray-700">
                  <div className="font-semibold">{g.summary.name}</div>
                  <div className="text-gray-500 pl-3">↳ {g.task.name}</div>
                  <div className="text-gold pl-3">◆ {g.milestone.name}</div>
                </td>
                <td className="px-2 py-1 text-[8px] text-gray-500 whitespace-nowrap tabular-nums">{fmtD(g.task.start_date)}</td>
                <td className="px-2 py-1 text-[8px] text-gray-700 whitespace-nowrap tabular-nums font-semibold">{fmtD(g.milestone.date)}</td>
                <td className={`px-2 py-1 text-[8px] text-right tabular-nums ${g.tight ? 'text-amber-700' : 'text-gray-500'}`}>
                  {g.task.duration_days}
                  {g.tight && <div className="text-[7px]">no room</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>

        {suggestion.groups.some(g => g.tight) && (
          <div className="text-[8px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Some milestones leave no working days before the one after them, usually because two share a date
            or one is already in the past. Those get a single day so the plan is still buildable, and want a
            proper duration once you know it.
          </div>
        )}
        {(suggestion.skipped || []).length > 0 && (
          <div className="text-[8px] text-gray-500">
            Skipped: {suggestion.skipped.map(s => s.milestone_code).join(', ')} — no forecast or contract date
            to plan towards.
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ────────────────────────────────────────────────────────────
   Panels
   ──────────────────────────────────────────────────────────── */

function TemplatePanel({ templates, hasTasks, onApply, onSaveAs, onClose }) {
  return (
    <Modal title="Plan templates" onClose={onClose} footer={
      <>
        {hasTasks && <button onClick={onSaveAs} className="px-3 py-1 text-[10px] font-semibold border border-violet-300 bg-violet-50 text-violet-700 rounded mr-auto">Save this plan as a template</button>}
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
      </>
    }>
      <div className="text-[8px] text-gray-400 mb-2">
        Applying a template adds its tasks below whatever is already here, keeping its structure,
        dependency types and lags.
      </div>
      {templates.length === 0 ? (
        <div className="py-8 text-center text-[9px] text-gray-400">No templates yet. Build a plan and save it as one.</div>
      ) : (
        <div className="divide-y divide-gray-100 max-h-[50vh] overflow-y-auto">
          {templates.map(t => (
            <button key={t.id} onClick={() => onApply(t)}
                    className="w-full text-left py-2 px-1 hover:bg-gold/5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-semibold text-navy">{t.name}</div>
                {t.description && <div className="text-[8px] text-gray-400">{t.description}</div>}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[11px] font-bold text-gold">{t.task_count}</div>
                <div className="text-[7px] text-gray-400">tasks</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

function SaveTemplatePanel({ sowId, taskCount, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', description: '', visibility: 'public' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const r = await api.post(`sows/${sowId}/project-tasks/save-as-template`, {
        name: form.name.trim(), description: form.description.trim() || null, visibility: form.visibility,
      });
      toast.success(`Template saved with ${r?.created ?? 0} tasks.`);
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <Modal title="Save as template" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={save} disabled={saving || !form.name.trim()}
                className="px-3 py-1 text-[10px] font-semibold bg-violet-600 text-white rounded disabled:opacity-40">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </>
    }>
      <div className="space-y-3">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
          <input className="form-input" autoFocus value={form.name}
                 onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                 placeholder="e.g. Standard site deployment" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <input className="form-input" value={form.description}
                 onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <div className="flex gap-4">
          {['public', 'private'].map(v => (
            <label key={v} className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={form.visibility === v} className="accent-violet-600"
                     onChange={() => setForm(f => ({ ...f, visibility: v }))} />
              <span className="text-[9px] font-semibold text-gray-700 capitalize">{v}</span>
            </label>
          ))}
        </div>
        <div className="text-[7.5px] text-gray-400 bg-gray-50 rounded px-2 py-1.5">
          Saves all {taskCount} rows with their structure, durations, dependency types and lags.
          Dates, owners and progress are not carried over.
        </div>
      </div>
    </Modal>
  );
}

function BaselinePanel({ baselines, current, tasks, prefill, onTake, onSetCurrent, onClose }) {
  const [name, setName] = useState(
    prefill?.name || (baselines.length ? `Re-baseline ${new Date().toLocaleDateString('en-GB')}` : 'Contract baseline'));
  const [note, setNote] = useState('');
  const dated = tasks.filter(t => t.start_date);

  return (
    <Modal title="Baselines" onClose={onClose} wide footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
    }>
      <div className="space-y-4">
        <div className="text-[8px] text-gray-500 bg-gray-50 rounded px-2 py-1.5">
          A baseline is the plan you promised, frozen. Taking a new one never destroys the last:
          the original signed position is the one that matters when a client asks why delivery moved.
        </div>

        <div className="border border-gray-200 rounded p-2">
          <div className="text-[8px] font-bold text-gray-500 uppercase mb-1.5">Take a baseline</div>
          <div className="grid grid-cols-2 gap-2">
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Name" />
            <input className="form-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Why (optional)" />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[8px] text-gray-400">
              Freezes {dated.length} dated row{dated.length !== 1 ? 's' : ''}
              {dated.length ? ` — ${fmtD(dated.map(t => t.start_date).sort()[0])} to ${fmtD(dated.map(t => t.end_date).filter(Boolean).sort().slice(-1)[0])}` : ''}.
            </span>
            <button onClick={() => onTake(name, note, prefill?.ccn_id)} disabled={!name.trim() || !dated.length}
                    className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
              Take baseline
            </button>
          </div>
        </div>

        <div>
          <div className="text-[8px] font-bold text-gray-500 uppercase mb-1">History</div>
          {baselines.length === 0 ? (
            <div className="text-[9px] text-gray-400 italic py-3">
              None yet. Until you take one, there is nothing to measure slip against.
            </div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full">
              <thead><tr className="border-b border-gray-100">
                <Th>Name</Th><Th className="w-20">Taken</Th><Th className="w-24">By</Th>
                <Th className="w-16 text-right">Tasks</Th><Th className="w-32">Span</Th><Th className="w-20"></Th>
              </tr></thead>
              <tbody>
                {baselines.map(b => (
                  <tr key={b.id} className={`border-b border-gray-50 ${b.is_current ? 'bg-emerald-50/50' : ''}`}>
                    <td className="px-2 py-1 text-[9px] font-semibold text-gray-700">
                      {b.name}
                      {b.is_current ? <span className="ml-1.5 text-[7px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">CURRENT</span> : null}
                      {b.ccn_number && <span className="ml-1.5 text-[7px] text-gray-400">{b.ccn_number}</span>}
                      {b.note && <div className="text-[7.5px] text-gray-400 font-normal">{b.note}</div>}
                    </td>
                    <td className="px-2 py-1 text-[8px] text-gray-500">{fmtDY(b.taken_at?.slice(0, 10))}</td>
                    <td className="px-2 py-1 text-[8px] text-gray-500">{b.taken_by_name || '—'}</td>
                    <td className="px-2 py-1 text-[8px] text-gray-500 text-right">{b.task_count}</td>
                    <td className="px-2 py-1 text-[8px] text-gray-500">{fmtD(b.plan_start)} → {fmtD(b.plan_finish)}</td>
                    <td className="px-2 py-1 text-right">
                      {!b.is_current && (
                        <button onClick={() => onSetCurrent(b.id)}
                                className="text-[8px] px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50">
                          Compare against this
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function MilestonePanel({ proposals, tasks, onLink, onAccept, onClose }) {
  const [selected, setSelected] = useState(() => new Set());
  const pending = proposals.filter(p => p.differs && !p.locked);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  return (
    <Modal title="Payment milestones and the plan" onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
        <button
          onClick={() => onAccept(selected.size ? [...selected] : null)}
          disabled={!pending.length}
          className="px-3 py-1 text-[10px] font-semibold bg-blue-600 text-white rounded disabled:opacity-40">
          {selected.size ? `Accept ${selected.size} date${selected.size !== 1 ? 's' : ''}` : `Accept all ${pending.length}`}
        </button>
      </>
    }>
      <div className="space-y-3">
        <div className="text-[8px] text-gray-500 bg-gray-50 rounded px-2 py-1.5">
          Link each payment milestone to the task or summary bar that earns it, and the plan will tell you
          what its forecast date should be. It never changes one on its own: these dates feed expected
          income and invoicing, so a person accepts them.
        </div>

        {proposals.length === 0 ? (
          <div className="text-[9px] text-gray-400 italic py-4 text-center">No milestones on this SOW.</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr className="border-b border-gray-100">
              <Th className="w-6"></Th><Th className="w-16">Code</Th><Th>Description</Th>
              <Th className="w-48">Earned by</Th><Th className="w-20">Forecast</Th>
              <Th className="w-20">Plan says</Th><Th className="w-16 text-right">Slip</Th>
            </tr></thead>
            <tbody>
              {proposals.map(p => (
                <tr key={p.id} className={`border-b border-gray-50 ${p.differs && !p.locked ? 'bg-blue-50/40' : ''}`}>
                  <td className="px-2 py-1">
                    {p.differs && !p.locked && (
                      <input type="checkbox" className="w-3 h-3 accent-blue-600"
                             checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                    )}
                  </td>
                  <td className="px-2 py-1 text-[9px] font-semibold text-navy">{p.milestone_code}</td>
                  <td className="px-2 py-1 text-[8px] text-gray-600 truncate max-w-[180px]">{p.description || '—'}</td>
                  <td className="px-2 py-1">
                    <select className="form-select text-[8px] w-full" value={p.task_id || ''}
                            onChange={e => onLink(p.id, e.target.value)}>
                      <option value="">— not linked —</option>
                      {tasks.map(t => (
                        <option key={t.id} value={t.id}>{t.wbs_code} {t.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1 text-[8px] text-gray-500 whitespace-nowrap">{fmtD(p.forecast_date)}</td>
                  <td className="px-2 py-1 text-[8px] whitespace-nowrap font-semibold">
                    {p.proposed_forecast_date
                      ? <span className={p.differs ? 'text-blue-700' : 'text-gray-400'}>{fmtD(p.proposed_forecast_date)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-2 py-1 text-[8px] text-right">
                    {p.locked ? <span className="text-gray-400 text-[7px]">{p.status}</span>
                      : p.slip_days == null ? <span className="text-gray-300">—</span>
                      : <span className={p.slip_days > 0 ? 'text-red-600 font-semibold' : 'text-emerald-600'}>
                          {p.slip_days > 0 ? '+' : ''}{p.slip_days}d
                        </span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </Modal>
  );
}

function PartiesPanel({ parties, onClose, onChanged }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', short_code: '', party_type: 'partner' });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post('external-parties', { ...form, name: form.name.trim(), short_code: form.short_code.trim() || null });
      setForm({ name: '', short_code: '', party_type: 'partner' });
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Could not add that party.');
    } finally {
      setSaving(false);
    }
  };

  const retire = async (p) => {
    if (!confirm(`Retire ${p.name}? Tasks it is already on keep it; it stops appearing in the picker.`)) return;
    await api.delete(`external-parties/${p.id}`).catch(() => {});
    onChanged();
  };

  return (
    <Modal title="External parties" onClose={onClose} footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
    }>
      <div className="space-y-3">
        <div className="text-[8px] text-gray-500 bg-gray-50 rounded px-2 py-1.5">
          Organisations a plan depends on but we do not employ: a client's own IT team, a landlord,
          a main contractor, a managing agent. Put them on a task and the dependency is visible
          instead of living in somebody's head.
        </div>
        <div className="flex gap-1.5 items-end">
          <div className="flex-1">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name</label>
            <input className="form-input" value={form.name} placeholder="e.g. Jones Lang LaSalle"
                   onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="w-20">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Short</label>
            <input className="form-input" value={form.short_code} placeholder="JLL" maxLength={8}
                   onChange={e => setForm(f => ({ ...f, short_code: e.target.value }))} />
          </div>
          <div className="w-28">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
            <select className="form-select" value={form.party_type}
                    onChange={e => setForm(f => ({ ...f, party_type: e.target.value }))}>
              {['client', 'supplier', 'partner', 'contractor', 'other'].map(t =>
                <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button onClick={add} disabled={saving || !form.name.trim()}
                  className="px-3 py-1.5 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Add</button>
        </div>

        <div className="max-h-[40vh] overflow-y-auto divide-y divide-gray-100">
          {parties.length === 0 ? (
            <div className="py-6 text-center text-[9px] text-gray-400">None on file yet.</div>
          ) : parties.map(p => (
            <div key={p.id} className="flex items-center gap-2 py-1.5">
              <span className="text-[9px] font-semibold text-gray-700 flex-1">
                {p.name}
                {p.short_code && <span className="ml-1.5 text-[7px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">{p.short_code}</span>}
              </span>
              <span className="text-[8px] text-gray-400 w-20">{p.party_type}</span>
              <span className="text-[8px] text-gray-400 w-16 text-right">{p.task_count || 0} task{p.task_count === 1 ? '' : 's'}</span>
              <button onClick={() => retire(p)} className="text-[8px] text-gray-300 hover:text-red-500 px-1">retire</button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
